import { useState, useEffect, useCallback } from "react";
import { Button, Image, Pagination, Alert, Table, Modal, NavLink, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth, formatPrice } from "../../Functions";
import plus from "../../assets/images/plus.svg";

const ManageListings = () => {
    const API = process.env.REACT_APP_API_BASE_URL;

    const [listings, setListings] = useState([]);
    const [prev, setPrev] = useState("");
    const [next, setNext] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [fetchFeedBack, setFetchFeedback] = useState({ success: null, message: "" });
    const [deleteFeedBack, setDeleteFeedback] = useState({ success: null, message: "" });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Feedback popup for delete listing button
    const [showModal, setShowModal] = useState(false);
    const handleCloseModal = () => {
        setShowModal(false);
    }

    // Logic for fetching listings from API
    const fetchListings = useCallback(async (pageNumber) => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(`${API}/properties/?page=${pageNumber}`);

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFetchFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful responses
            if (!response.ok) {
                const errorData = await response.json();
                setFetchFeedback({ success: false, message: `Unable to load properties, please refresh page.` });
                console.error(`Unable to load propertiies: ${errorData?.detail}` || `Unable to load propertiies: ${errorData.status}`);
            }

            const data = await response.json();

            setListings(data.results);
            setPrev(data.links["previous"]);
            setNext(data.links["next"]);
            setCurrentPage(data.current_page);
            setTotalPages(data.total_pages);
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error has occured." });
            console.error(`An error occured while trying to fetch properties: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [API, navigate]);

    // Delete listing logic
    const handleListingDelete = async (listingID, listingName) => {
        try {
            const response = await fetchWithAuth(`${API}/properties/delete/${listingID}/`, {
                method: 'DELETE',
            });

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setDeleteFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful API responses
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.detail || `Error deleting listing: ${response.status} ${response.statusText}`;
                setDeleteFeedback({ success: false, message: `Unable to delete ${listingName}, please try again.` });
                console.error(errorMessage);
            }

            // Update listings state
            const updatedListings = listings.filter((listing) => listing.id !== listingID);
            setListings(updatedListings);

            // Proceed to set successful feedback
            setDeleteFeedback({ success: true, message: `${listingName} deleted successfully.`})
        } catch (error) {
            setDeleteFeedback({ success: false, message: `An unexpected error occured while deleting ${listingName}.`});
        }

        // Activate popup to display delete feedback
        setShowModal(true);
    }

    // Update listing logic
    const handleListingUpdate = (listingID) => {
        navigate("/dashboard/manage-listings/edit", { state: {listingID} });
    }

    // Load listings into page
    useEffect(() => {
        fetchListings(currentPage);
    }, [currentPage, fetchListings]);

    // Pagination logic
    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    }

    const pages = [];
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        pages.push(
            <Pagination.Item
                key={ pageNumber }
                active={ pageNumber === currentPage }
                onClick={ () => handlePageClick(pageNumber) }
            >
                { pageNumber }
            </Pagination.Item>
        )
    }

    return (
        <>
            <h1 className="my-lg-3">Manage Listings</h1>
            <Button className="px-4 py-2 my-3" href="/dashboard/manage-listings/add">
                <Image src={ plus } /> Add new listing
            </Button>

            {/* Fetch Listings Alert */}
            { (!fetchFeedBack.success && fetchFeedBack.success !== null) && // Display this alert only when fetchListings fails
                <Alert variant="danger">{ fetchFeedBack.message }</Alert>
            }

            {/* Property list */}
            {isLoading ? (
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" className="mx-auto" />
                    <p className="mt-2">Loading property listings...</p>
                </div>
            ) : (
                listings && (
                    <Table  className="responsive-table my-3">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Price</th>
                                <th>Location</th>
                                <th>Created</th>
                                <th>Last Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            { listings.map((listing, index) => (
                                <tr key={index}>
                                    <td data-label="Name" className="listing-name">
                                        <NavLink href={`/properties/${listing.id}`} className="text-decoration-underline text-primary">{ listing.property_name }</NavLink>
                                    </td>
                                    <td data-label="Price">{ formatPrice(listing.price) }</td>
                                    <td data-label="City">{ listing.city }, { listing.state }</td>
                                    <td data-label="Created">{ listing.created_at.slice(0, 19).replace('T', ' ') }</td>
                                    <td data-label="Updated">{ listing.updated_at.slice(0, 19).replace('T', ' ')  }</td>
                                    <td data-label="Actions">
                                        <div>
                                            <Button className="me-2" variant="warning" onClick={() => handleListingUpdate(listing.id)}>Edit</Button>
                                            <Button variant="danger" onClick={() => handleListingDelete(listing.id, listing.property_name)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        { deleteFeedBack.success !== null &&
                            <Modal show={ showModal } onHide={ handleCloseModal } size="sm" centered>
                                <Modal.Header closeVariant="danger" closeButton>
                                    <Modal.Title as="h5">{ deleteFeedBack.success ? "Success" : "Failed!"}</Modal.Title>
                                </Modal.Header>
                                <Modal.Body as="p" className="fw-semibold">{ deleteFeedBack.message }</Modal.Body>
                            </Modal>
                        }
                    </Table>
                )
            )}

            {/* Pagination */}
            { totalPages > 1 &&
                <Pagination size="sm" className="my-5 mx-auto justify-content-md-end gap-2">
                    <Pagination.Prev onClick={() => prev && setCurrentPage(currentPage - 1)} disabled={!prev}>Prev</Pagination.Prev>
                    { pages }
                    <Pagination.Next onClick={() => next && setCurrentPage(currentPage + 1)} disabled={!next}>Next</Pagination.Next>
                </Pagination>
            }
        </>
    )
}

export default ManageListings;
